
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { SchemaOrg, organizationSchema } from '../../utils/seo';

const services = [
  {
    id: 1,
    title: '生命保険',
    subtitle: '万が一に備える家族の安心設計',
    description: 'ご家族の将来を守るため、死亡保障・医療保障・介護保障を組み合わせた最適なプランをご提案。岡山市の生活費や教育費を考慮した保障額設定で、安心の未来設計をサポートします。',
    icon: 'ri-heart-pulse-line',
    features: ['死亡保障プラン', '収入保障保険', '終身保険', '定期保険'],
  },
  {
    id: 2,
    title: '医療保険',
    subtitle: '岡山市内の提携病院で安心の医療保障',
    description: '岡山大学病院をはじめとする市内主要医療機関と提携。入院・手術・先進医療に対する充実した保障で、治療費の心配なく治療に専念できる環境を整えます。',
    icon: 'ri-hospital-line',
    features: ['入院保障', '手術保障', '先進医療特約', 'がん保険'],
  },
  {
    id: 3,
    title: '自動車保険',
    subtitle: '岡山・倉敷エリアの事故対応24時間サポート',
    description: '地域の道路事情を熟知したロードサービスと、24時間365日の事故対応で安心のカーライフを実現。岡山県内の修理工場ネットワークで迅速な対応が可能です。',
    icon: 'ri-car-line',
    features: ['対人・対物保険', '車両保険', 'ロードサービス', '弁護士費用特約'],
  },
  {
    id: 4,
    title: '火災保険',
    subtitle: '地域特性を考慮した住まいの保障',
    description: '岡山県の気候特性（台風・豪雨）や地震リスクを考慮し、お客様の住宅に最適な補償内容をご提案。住宅ローンとセットでお得なプランもご用意しています。',
    icon: 'ri-home-line',
    features: ['火災・落雷保障', '風災・水災保障', '地震保険', '家財保険'],
  },
  {
    id: 5,
    title: '個人年金保険',
    subtitle: '老後の生活設計をサポート',
    description: '公的年金だけでは不安な老後資金を計画的に準備。岡山市の平均的な生活費を基に、ゆとりある老後生活を送るための個人年金プランをご提案します。',
    icon: 'ri-coins-line',
    features: ['確定年金', '終身年金', '変額年金', '外貨建て年金'],
  },
  {
    id: 6,
    title: '法人向け保険',
    subtitle: '岡山の中小企業経営者様向けプラン',
    description: '経営者の万が一に備える事業保障、従業員の福利厚生、事業継承対策まで、岡山の中小企業が直面する課題に対応した包括的な法人保険をご提供します。',
    icon: 'ri-building-line',
    features: ['経営者保険', '従業員保険', '事業保険', '退職金準備'],
  },
];

export default function ServicesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '総合保険サービス',
    description: '岡山市民の暮らしを守る総合保険サービス。生命保険、医療保険、自動車保険、火災保険、個人年金保険、法人向け保険を提供。',
    provider: organizationSchema,
    areaServed: [
      { '@type': 'City', name: '岡山市' },
      { '@type': 'City', name: '倉敷市' },
      { '@type': 'City', name: '総社市' },
    ],
    serviceType: ['生命保険', '医療保険', '自動車保険', '火災保険', '個人年金保険', '法人向け保険'],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '事業内容',
        item: `${siteUrl}/services`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <SchemaOrg type="Service" data={serviceSchema} />
      <SchemaOrg type="WebPage" data={breadcrumbSchema} />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative flex items-center justify-center overflow-hidden" style={{ height: '40vh' }}>
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/0e57862e8c324ce4246309cbb0c88709.jpg"
              alt="岡山市の街並み"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
          </div>
          
          <div className="relative z-10 text-center text-white px-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">事業内容</h1>
            <nav className="text-sm">
              <Link to="/" className="hover:text-[#FF9B7A] transition-colors cursor-pointer">ホーム</Link>
              <span className="mx-2">&gt;</span>
              <span>事業内容</span>
            </nav>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 md:py-24 bg-[#FAF8F3]">
          <div className="max-w-5xl mx-auto px-6">
            <div className="mb-12 md:mb-16">
              <p className="text-[#FF9B7A] text-sm font-medium mb-2 tracking-wide">Our Services</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-[#2C3E50] leading-tight mb-6 md:mb-8" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                岡山市民の暮らしを守る<br />
                総合保険サービス
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed max-w-3xl">
                創業以来、岡山市・倉敷市・総社市を中心とした地域の皆さまに、人生のあらゆるステージで必要な保険サービスをご提供してまいりました。
                お客様一人ひとりのライフスタイルに合わせた最適なプランで、安心の未来をサポートいたします。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {services.map((service) => (
                <div key={service.id} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4 md:gap-6">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-[#FF9B7A] rounded-full flex items-center justify-center flex-shrink-0">
                      <i className={`${service.icon} text-xl md:text-2xl text-white`}></i>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-bold text-[#2C3E50] mb-2">
                        {service.title}
                      </h3>
                      <p className="text-sm md:text-base text-[#FF9B7A] font-medium mb-3 md:mb-4">
                        {service.subtitle}
                      </p>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4 md:mb-6">
                        {service.description}
                      </p>
                      
                      <div className="space-y-2 mb-4 md:mb-6">
                        {service.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-[#FF9B7A] rounded-full"></div>
                            <span className="text-xs md:text-sm text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <Link
                        to="/contact"
                        className="text-sm md:text-base text-[#FF9B7A] font-medium flex items-center gap-1 hover:gap-2 transition-all cursor-pointer"
                      >
                        詳しく見る
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-[#FF9B7A]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4 px-4">
              保険のお悩み、<br className="sm:hidden" />まずはご相談ください
            </h3>
            <p className="text-white/90 text-base md:text-lg mb-6 md:mb-8">
              岡山市・倉敷市・総社市への出張相談も承ります
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <Link
                to="/contact"
                className="w-full sm:w-auto bg-white text-[#FF9B7A] px-8 py-4 rounded-full font-bold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                無料相談予約
              </Link>
              <a
                href="tel:0860123456"
                className="w-full sm:w-auto bg-transparent text-white border-2 border-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-[#FF9B7A] transition-colors whitespace-nowrap cursor-pointer"
              >
                お電話で相談
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}